import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

const RESIZE_OBSERVER_OPTIONS = {
  threshold: 0.1
};

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content'];

  #resizeObserver;
  #contentWidth = 0;
  #containerWidth = 0;
  #copies = 1;
  #animationFrame = null;

  connectedCallback() {
    super.connectedCallback();

    // Create ResizeObserver for more efficient size tracking
    this.#resizeObserver = new ResizeObserver(this.#handleResize);
    this.#resizeObserver.observe(this, RESIZE_OBSERVER_OPTIONS);
    this.#resizeObserver.observe(this.refs.content.firstElementChild, RESIZE_OBSERVER_OPTIONS);

    this.#initializeMarquee();

    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Clean up all resources
    this.#resizeObserver?.disconnect();
    this.#animation?.cancel();
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
    }
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);

    // Clean up cloned elements
    this.#removeAllClones();
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp = () => {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  #initializeMarquee() {
    this.#updateContentWidth();
    this.#updateRequiredCopies();
    this.#updateMarquee();
    this.#setSpeed();
  }

  #updateContentWidth() {
    const firstElement = this.refs.content.firstElementChild;
    if (firstElement instanceof HTMLElement) {
      this.#contentWidth = firstElement.offsetWidth;
      this.#containerWidth = this.offsetWidth;
    }
  }

  #updateRequiredCopies() {
    if (this.#contentWidth === 0) return;
    this.#copies = Math.max(2, Math.ceil((this.#containerWidth * 2) / this.#contentWidth));
  }

  #setSpeed(value = this.#calculateSpeed()) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  #calculateSpeed() {
    const speedFactor = Number(this.getAttribute('data-speed-factor')) || 20;
    return Math.ceil(this.#containerWidth / speedFactor / 2);
  }

  #handleResize = debounce(() => {
    this.#updateContentWidth();
    const oldCopies = this.#copies;
    this.#updateRequiredCopies();

    if (oldCopies !== this.#copies) {
      this.#updateMarquee();
    }

    this.#setSpeed();
    this.#restartAnimation();
  }, 250);

  #restartAnimation() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
    }

    this.#animationFrame = requestAnimationFrame(() => {
      const animations = this.refs.wrapper.getAnimations();
      for (const animation of animations) {
        animation.currentTime = 0;
      }
      this.#animationFrame = null;
    });
  }

  #updateMarquee() {
    const { content } = this.refs;
    const firstItem = content.firstElementChild;
    if (!firstItem) return;

    // Remove existing clones
    while (content.children.length > 1) {
      content.lastElementChild.remove();
    }

    // Add new clones
    const fragment = document.createDocumentFragment();
    for (let i = 1; i < this.#copies; i++) {
      const clone = firstItem.cloneNode(true);
      fragment.appendChild(clone);
    }
    content.appendChild(fragment);
  }

  #removeAllClones() {
    const { content } = this.refs;
    while (content.children.length > 1) {
      content.lastElementChild.remove();
    }
  }
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
